import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faVolumeDown,
  faCompactDisc,
  faExternalLinkAlt,
  faMusic,
  faLink,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import "./DiscoverLanding.scss";

const API_BASE =
  process.env.REACT_APP_DISCOVERY_API_URL || "https://api.spinninbeans.com";

// ─── Curation constants ────────────────────────────────────
const SIMILARITY_FLOOR = 0.75;
const DEFAULT_VISIBLE = 6;
const HARD_MAX = 10;

// ─── Helpers ───────────────────────────────────────────────
function getYouTubeId(url) {
  const match = url?.match(/(?:v=|\/)([\w-]{11})/);
  return match ? match[1] : null;
}

function getSafeExternalUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

function formatTimestamp(seconds) {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function getLabel(result) {
  const meta = result.metadata || {};
  const bp = meta.metadata?.beatport;
  return meta.label_name || meta.label || bp?.release?.label?.name || null;
}

function getPreviewUrl(result) {
  const meta = result.metadata || {};
  if (result.preview_url) return result.preview_url;
  if (meta.preview_url) return meta.preview_url;
  // Hydrate stream URL from platform + track ID (same proxy the Discover page uses)
  if (result.platform === "soundcloud" && result.platform_track_id) {
    return `/api/soundcloud/tracks/${result.platform_track_id}/stream`;
  }
  if (result.platform === "bandcamp" && result.platform_track_id) {
    return `/api/bandcamp/tracks/${result.platform_track_id}/stream`;
  }
  return null;
}

function getResultId(result, index = 0) {
  const meta = result.metadata || {};
  return (
    result.id ||
    result.track_id ||
    [
      result.platform || "unknown",
      result.external_url || "",
      meta.primary_title || meta.normalized_title || "",
      meta.artist_norm || "",
      index,
    ].join("|")
  );
}

function normalizeResult(result, index) {
  const metadata = result.metadata || {};
  return {
    ...result,
    id: getResultId(result, index),
    metadata,
    preview_url: getPreviewUrl(result),
    primary_title: metadata.primary_title || "Unknown",
    artist_norm: metadata.artist_norm || "Unknown Artist",
    primary_image_url: metadata.primary_image_url || null,
  };
}

const platformColor = {
  beatport: "#94d500",
  soundcloud: "#ff5500",
  bandcamp: "#1da0c3",
};

// ─── Preview Player (bottom bar) ───────────────────────────
const PreviewPlayer = ({ track, isPlaying, onPlayPause, audioRef }) => {
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [showVolume, setShowVolume] = useState(false);
  const [muted, setMuted] = useState(false);
  const progressRef = useRef(null);
  const volumeRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
    };
  }, [audioRef]);

  useEffect(() => {
    if (!showVolume) return;
    const handleClick = (e) => {
      if (volumeRef.current && !volumeRef.current.contains(e.target)) {
        setShowVolume(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showVolume]);

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !isFinite(audio.duration)) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  };

  const handleVolumeChange = (e) => {
    const val = Number(e.target.value);
    setVolume(val);
    setMuted(val === 0);
    if (audioRef.current) {
      audioRef.current.volume = val / 100;
      audioRef.current.muted = false;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (muted) {
      audio.muted = false;
      audio.volume = (volume || 80) / 100;
      setMuted(false);
      if (volume === 0) setVolume(80);
    } else {
      audio.muted = true;
      setMuted(true);
    }
  };

  const volIcon = muted || volume === 0 ? faVolumeMute : volume < 50 ? faVolumeDown : faVolumeUp;

  if (!track) return null;

  return (
    <div className="preview-player">
      <div className="player-track-info">
        {track.primary_image_url ? (
          <img src={track.primary_image_url} alt="" className="player-art" />
        ) : (
          <div className="player-art placeholder">
            <FontAwesomeIcon icon={faMusic} />
          </div>
        )}
        <div className="player-text">
          <span className="player-title">{track.primary_title}</span>
          <span className="player-artist">{track.artist_norm}</span>
        </div>
      </div>

      <div className="player-controls">
        <button className="player-play-btn" onClick={onPlayPause}>
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </button>
        <span className="player-time">{fmtTime(currentTime)}</span>
        <div className="player-progress" ref={progressRef} onClick={handleSeek}>
          <div className="player-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="player-time">{fmtTime(duration)}</span>

        <div className="volume-control" ref={volumeRef}>
          <button className="volume-btn" onClick={toggleMute} onMouseEnter={() => setShowVolume(true)}>
            <FontAwesomeIcon icon={volIcon} />
          </button>
          {showVolume && (
            <div className="volume-popup">
              <input
                type="range"
                min="0"
                max="100"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                className="volume-slider"
                style={{ "--vol": `${muted ? 0 : volume}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Track Row ─────────────────────────────────────────────
const TrackRow = ({ result, index, isPlaying, onPlay, onPause, showPlayback }) => {
  const meta = result.metadata || {};
  const label = getLabel(result);
  const matchPct = Math.round(result.similarity_score * 100);
  const safeExternalUrl = getSafeExternalUrl(result.external_url);

  return (
    <tr className={`track-row ${isPlaying ? "active" : ""}`}>
      <td className="col-num">{index + 1}</td>
      <td className="col-art">
        {meta.primary_image_url ? (
          <img src={meta.primary_image_url} alt="" className="row-artwork" loading="lazy" />
        ) : (
          <div className="row-artwork placeholder">
            <FontAwesomeIcon icon={faCompactDisc} />
          </div>
        )}
      </td>
      {showPlayback && (
        <td className="col-play">
          <button
            className="row-play-btn"
            onClick={() => (isPlaying ? onPause() : onPlay(result))}
            disabled={!result.preview_url}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>
        </td>
      )}
      <td className="col-title">
        <span className="row-title">{result.primary_title}</span>
        <span className="row-artist">{result.artist_norm}</span>
      </td>
      <td className="col-label">{label || ""}</td>
      <td className="col-meta">
        <div className="row-tags">
          {meta.primary_genre && <span className="meta-tag genre">{meta.primary_genre}</span>}
          {meta.bpm && <span className="meta-tag">{Math.round(meta.bpm)} BPM</span>}
          {meta.camelot_key && <span className="meta-tag key">{meta.camelot_key}</span>}
        </div>
      </td>
      <td className="col-match">
        <span className="match-badge">{matchPct}%</span>
      </td>
      <td className="col-link">
        {safeExternalUrl && (
          <a
            href={safeExternalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="row-external"
            title={`Open on ${result.platform}`}
            style={{ color: platformColor[result.platform] || "#888" }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        )}
      </td>
    </tr>
  );
};

// ─── Main Component ────────────────────────────────────────
const DiscoverLanding = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null); // publish queue item
  const [results, setResults] = useState([]); // curated similar tracks
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);

  // Audio player
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(new Audio());

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch both endpoints in parallel
      const contentId = searchParams.get("content_id") || `moment:${id}`;
      const [queueRes, recsRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/set-radar/publish-queue/${encodeURIComponent(contentId)}`),
        fetch(`${API_BASE}/api/recommendations/moments/${id}/similar`),
      ]);

      // Recommendations are required
      if (recsRes.status === "rejected" || !recsRes.value.ok) {
        throw new Error("Could not load recommendations");
      }
      const recsData = await recsRes.value.json();

      // Publish queue context is optional (enriches the hero)
      let queueData = null;
      if (queueRes.status === "fulfilled" && queueRes.value.ok) {
        queueData = await queueRes.value.json();
      }

      // Curate results: filter by floor, cap at hard max
      const curated = (recsData.results || [])
        .filter((r) => r.similarity_score >= SIMILARITY_FLOOR)
        .slice(0, HARD_MAX)
        .map((result, index) => normalizeResult(result, index));

      setContext({
        set_title: queueData?.set?.title || recsData.set_title || "",
        dj_name: queueData?.set?.dj_name || recsData.dj_name || "",
        deep_link: queueData?.set?.deep_link || null,
        timestamp_seconds: queueData?.moment?.timestamp_seconds ?? recsData.timestamp_seconds ?? 0,
        timestamp_text: queueData?.moment?.timestamp_text ?? formatTimestamp(recsData.timestamp_seconds),
        hook: queueData?.captions?.hook || null,
        track_display: queueData?.track?.display_title || null,
      });
      setShowAll(false);
      setResults(curated);
    } catch (err) {
      console.error("Failed to load landing page:", err);
      setError("Could not find what you're looking for.");
    } finally {
      setLoading(false);
    }
  }, [id, searchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Audio playback
  const playTrack = (result) => {
    const audio = audioRef.current;
    let url = result.preview_url || getPreviewUrl(result);
    if (!url) return;

    if (url.startsWith("/api/")) {
      url = `${API_BASE}${url}`;
    }

    if (currentTrack?.id === result.id && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    audio.src = url;
    audio.play().catch(() => {});
    setCurrentTrack(result);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine visible tracks
  const visibleResults = showAll ? results : results.slice(0, DEFAULT_VISIBLE);
  const hasMore = results.length > DEFAULT_VISIBLE;
  const hasAnyPreview = results.some((result) => !!result.preview_url);
  const safeDeepLink = getSafeExternalUrl(context?.deep_link);

  // YouTube thumbnail
  const videoId = safeDeepLink ? getYouTubeId(safeDeepLink) : null;
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

  // ─── Loading ───────────────────────────────────────────
  if (loading) {
    return (
      <div className="landing-page">
        <div className="landing-loading">
          <FontAwesomeIcon icon={faCompactDisc} spin className="loading-icon" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────
  if (error || !context) {
    return (
      <div className="landing-page">
        <div className="landing-error">
          <FontAwesomeIcon icon={faMusic} className="error-icon" />
          <p>{error || "Something went wrong."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* ─── Hero ─────────────────────────────────────────── */}
      <div className="landing-hero">
        <div className="hero-artwork">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={context.set_title} />
          ) : (
            <div className="hero-artwork-placeholder">
              <FontAwesomeIcon icon={faMusic} />
            </div>
          )}
        </div>

        <div className="hero-info">
          <h1 className="hero-title">{context.set_title}</h1>
          <p className="hero-dj">{context.dj_name}</p>
          {context.timestamp_text && (
            <span className="hero-timestamp">at {context.timestamp_text}</span>
          )}
          <div className="hero-actions">
            {safeDeepLink && (
              <a
                href={safeDeepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-btn primary"
              >
                <FontAwesomeIcon icon={faPlayCircle} /> Watch Set
              </a>
            )}
            <button className="hero-btn secondary" onClick={handleCopyLink}>
              <FontAwesomeIcon icon={copied ? faCheck : faLink} />
              {copied ? "Copied" : "Share"}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Similar Tracks Table ─────────────────────────── */}
      {results.length > 0 ? (
        <div className="landing-tracks">
          <table className="tracks-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th className="col-art"></th>
                {hasAnyPreview && <th className="col-play"></th>}
                <th className="col-title">TITLE / ARTISTS</th>
                <th className="col-label">LABEL</th>
                <th className="col-meta">GENRE / BPM & KEY</th>
                <th className="col-match">MATCH</th>
                <th className="col-link"></th>
              </tr>
            </thead>
            <tbody>
              {visibleResults.map((result, i) => (
                <TrackRow
                  key={result.id}
                  result={result}
                  index={i}
                  isPlaying={isPlaying && currentTrack?.id === result.id}
                  onPlay={playTrack}
                  onPause={() => {
                    audioRef.current.pause();
                    setIsPlaying(false);
                  }}
                  showPlayback={hasAnyPreview}
                />
              ))}
            </tbody>
          </table>

          {hasMore && !showAll && (
            <button className="show-more-btn" onClick={() => setShowAll(true)}>
              Show {results.length - DEFAULT_VISIBLE} more
            </button>
          )}
        </div>
      ) : (
        <div className="landing-empty">
          <p>No similar tracks found for this moment.</p>
        </div>
      )}

      {/* ─── Preview Player ───────────────────────────────── */}
      {createPortal(
        <PreviewPlayer
          track={currentTrack}
          isPlaying={isPlaying}
          onPlayPause={togglePlayPause}
          audioRef={audioRef}
        />,
        document.body
      )}
    </div>
  );
};

export default DiscoverLanding;
